import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import {
  createCourseBundlePaymentSession,
  createCourseMonthlySubscriptionSession,
  createFlexibleStripeSession,
  createSubscriptionStripeSession,
  createTopUpCourseSession,
  getCourseSubscriptionSession,
  cancelLpSubscription,
  getStripeCustomerPortalData,
  subscribeCourse,
} from './api';
import { RootState } from '../..';
import { IFlexiblePlan } from '../FlexiblePlansSlice';
import { COURSE_PRICING_TYPES, ICourse } from '../CoursesSlice';
import { IInvoice } from '../InvoicesSlice';
import trackGAEvent from '../../../helper/GoogleAnalytics';

export const COURSE_SUBSCRIPTION_SESSION_PAYMENT_FROM = {
  COURSE: 'COURSE',
  FLEXIBLE: 'FLEXIBLE',
  SUBSCRIPTION: 'SUBSCRIPTION',
  COURSE_TOPUP: 'COURSE_TOPUP',
  COURSE_MONTHLY_SUBSCRIPTION: 'COURSE_MONTHLY_SUBSCRIPTION',
  COURSE_MONTHLY_SUBSCRIPTION_RENEWAL: 'COURSE_MONTHLY_SUBSCRIPTION_RENEWAL',
} as const;

export type TCourseSubscriptionSessionPaymentFrom = typeof COURSE_SUBSCRIPTION_SESSION_PAYMENT_FROM[keyof typeof COURSE_SUBSCRIPTION_SESSION_PAYMENT_FROM];

export interface ICourseSubscriptionSession {
  _id: string;
  course?: string | ICourse;
  flexiblePlan?: string | IFlexiblePlan;
  status: 'PENDING' | 'PAID';
  stripeProductId: string;
  paymentFrom: TCourseSubscriptionSessionPaymentFrom;
  invoice?: string | IInvoice;
  stripeSession?: {
    _id: string;
    url: string;
    // eslint-disable-next-line camelcase
    amount_total: number;
    currency: string;
  };
  quantity: number;
}

export interface IStripeCustomerPortalData {
  url: string;
}

export interface IStripeV2State {
  status: 'idle' | 'loading' | 'rejected';
  courseSubscriptionSession?: ICourseSubscriptionSession;
  stripeCustomerPortalData?: IStripeCustomerPortalData;
}

const initialState: IStripeV2State = {
  status: 'idle',
  courseSubscriptionSession: undefined,
  stripeCustomerPortalData: undefined,
};

export const StripeSlice = createSlice({
  name: 'stripeV2',
  initialState,
  reducers: {
    resetCourseSubscriptionSession: (state) => {
      state.courseSubscriptionSession = undefined;
    },
    resetStripeCustomerPortalData: (state) => {
      state.stripeCustomerPortalData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(subscribeCourse.fulfilled, (state, action) => {
        const result = action.payload;

        if (result.subscriptionType === COURSE_PRICING_TYPES.CREDIT) {
          const queryParams = new URLSearchParams(window.location.search);

          queryParams.set('paymentStatus', 'success');

          const newUrl = `${window.location.origin}${
            window.location.pathname
          }?${queryParams.toString()}`;

          window.history.pushState(null, '', newUrl);

          window.location.reload();
        } else if (result.session && result.session.stripeSession) {
          window.location.href = result.session.stripeSession.url;
        }
      })
      .addCase(getCourseSubscriptionSession.fulfilled, (state, action) => {
        const result = action.payload;

        state.courseSubscriptionSession = result;
      })
      .addCase(createFlexibleStripeSession.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && result.stripeSession && result.stripeSession.url) {
          window.location.href = result.stripeSession.url;
        }
      })
      .addCase(createCourseBundlePaymentSession.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && result.stripeSession && result.stripeSession.url) {
          window.location.href = result.stripeSession.url;
        }
      })
      .addCase(createSubscriptionStripeSession.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && result.stripeSession && result.stripeSession.url) {
          window.location.href = result.stripeSession.url;
        }
      })
      .addCase(getStripeCustomerPortalData.fulfilled, (state, action) => {
        const result = action.payload;

        state.stripeCustomerPortalData = result;
      })
      .addCase(createTopUpCourseSession.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && result.stripeSession && result.stripeSession.url) {
          trackGAEvent('checkout_redirected', {
            hours: result.quantity || 1,
          });

          window.location.href = result.stripeSession.url;
        }
      })
      .addCase(
        createCourseMonthlySubscriptionSession.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (result && result.stripeSession && result.stripeSession.url) {
            trackGAEvent('checkout_redirected', {
              hours: result.quantity || 1,
            });

            window.location.href = result.stripeSession.url;
          }
        }
      );

    // Matchers
    builder
      .addMatcher(
        isPending(
          subscribeCourse,
          getCourseSubscriptionSession,
          createFlexibleStripeSession,
          createSubscriptionStripeSession,
          getStripeCustomerPortalData,
          cancelLpSubscription,
          createTopUpCourseSession,
          createCourseMonthlySubscriptionSession
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isFulfilled(
          subscribeCourse,
          getCourseSubscriptionSession,
          createFlexibleStripeSession,
          createSubscriptionStripeSession,
          getStripeCustomerPortalData,
          cancelLpSubscription,
          createTopUpCourseSession,
          createCourseMonthlySubscriptionSession
        ),
        (state) => {
          state.status = 'idle';
        }
      )
      .addMatcher(
        isRejected(
          subscribeCourse,
          getCourseSubscriptionSession,
          createFlexibleStripeSession,
          createSubscriptionStripeSession,
          getStripeCustomerPortalData,
          cancelLpSubscription,
          createTopUpCourseSession,
          createCourseMonthlySubscriptionSession
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetCourseSubscriptionSession,
  resetStripeCustomerPortalData,
} = StripeSlice.actions;

export const getStripeV2State = (state: RootState): RootState['stripeV2'] =>
  state.stripeV2;

export * from './api';
export default StripeSlice.reducer;
